import { ThemeMode } from "../interfaces/theme";
import {
  DARK_THEME,
  DARK_TAG_INK_LIGHTNESS,
  DARK_TAG_MIN_SATURATION,
  DARK_TAG_SURFACE_LIGHTNESS,
} from "../constants/theme";

interface TagColors {
  backgroundColor: string;
  color: string;
}

interface HslColor {
  hue: number;
  saturation: number;
  lightness: number;
}

/**
 * Read a configured colour into hue, saturation and lightness.
 *
 * Group colours are configured as hsl(), but hex and rgb() are accepted so a
 * change of format upstream does not silently produce wrong colours.
 *
 * @param value Colour as configured, e.g. "hsl(255, 76%, 91%)".
 * @returns The colour in HSL, or null when the value cannot be read.
 */
const readHsl = (value: string): HslColor | null => {
  if (!value) {
    return null;
  }
  const trimmed: string = value.trim();

  const hsl: RegExpMatchArray | null = trimmed.match(
    /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%/i,
  );
  if (hsl) {
    return {
      hue: Number(hsl[1]),
      saturation: Number(hsl[2]),
      lightness: Number(hsl[3]),
    };
  }

  let channels: number[] | null = null;
  const hex: RegExpMatchArray | null = trimmed.match(
    /^#([0-9a-f]{3}|[0-9a-f]{6})$/i,
  );
  if (hex) {
    const digits: string =
      hex[1].length === 3
        ? hex[1]
            .split("")
            .map((digit: string) => digit + digit)
            .join("")
        : hex[1];
    channels = [0, 2, 4].map((i: number) =>
      parseInt(digits.slice(i, i + 2), 16),
    );
  } else {
    const rgb: RegExpMatchArray | null = trimmed.match(
      /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i,
    );
    if (rgb) {
      channels = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
    }
  }
  if (!channels) {
    return null;
  }

  const [red, green, blue] = channels.map((c: number) => c / 255);
  const max: number = Math.max(red, green, blue);
  const min: number = Math.min(red, green, blue);
  const lightness: number = (max + min) / 2;
  const delta: number = max - min;
  if (delta === 0) {
    return { hue: 0, saturation: 0, lightness: lightness * 100 };
  }
  const saturation: number =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let hue: number;
  if (max === red) {
    hue = ((green - blue) / delta) % 6;
  } else if (max === green) {
    hue = (blue - red) / delta + 2;
  } else {
    hue = (red - green) / delta + 4;
  }
  hue = ((hue * 60) % 360 + 360) % 360;
  return { hue, saturation: saturation * 100, lightness: lightness * 100 };
};

/**
 * Colours for a tag whose palette is configured per group in the backend.
 *
 * A configured tag is a pale surface carrying dark ink, which on a dark page
 * reads as a bright block. Rather than replacing the colour, its hue and
 * saturation are kept and only the lightness is moved: the surface becomes a
 * deep tint and the ink a light one, so the tag still reads as the colour it was
 * configured with. Configured values are returned untouched in light mode, and
 * also in dark mode when they cannot be read.
 *
 * @param backgroundColor Configured background colour.
 * @param textColor Configured text colour.
 * @param theme Active theme.
 * @returns The colours to apply.
 */
export const getTagColors = (
  backgroundColor: string,
  textColor: string,
  theme: ThemeMode,
): TagColors => {
  const configured: TagColors = { backgroundColor, color: textColor };
  if (theme !== DARK_THEME) {
    return configured;
  }
  const source: HslColor | null = readHsl(textColor) || readHsl(backgroundColor);
  if (!source) {
    return configured;
  }
  const saturation: number = Math.max(
    source.saturation,
    DARK_TAG_MIN_SATURATION,
  );
  return {
    backgroundColor: `hsl(${source.hue}, ${saturation}%, ${DARK_TAG_SURFACE_LIGHTNESS}%)`,
    color: `hsl(${source.hue}, ${saturation}%, ${DARK_TAG_INK_LIGHTNESS}%)`,
  };
};
